import { GraphQLInvitesRequester } from '@cobliteam/api-lib';
import type { ICreatedUser, IEnrollmentFormInfo, IInviteAcceptForm, Invite, InviteForm } from '@cobliteam/api-lib/src/__generated__/graphql';
import Alert from '@cobliteam/ui/components/Alert';
import { type UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';

const QUERY_KEY = 'INVITES';

const useInvite = () => {
  const invitesRequester = new GraphQLInvitesRequester();

  const useGetPendingInvites = (options?: Partial<UseQueryOptions<Invite[]>>) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<Invite[]>({
      queryKey: [QUERY_KEY],
      queryFn: async () => await invitesRequester.getPendingInvites(),
      ...options,
    });
  };

  const useGetInviteInfo = (token: string) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<IEnrollmentFormInfo, unknown, string>({
      queryKey: [QUERY_KEY],
      queryFn: async () => await invitesRequester.getInviteInfo(token),
    });
  };

  const useSendInvites = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<any, any, InviteForm[]>({
      mutationKey: [QUERY_KEY],
      mutationFn: async (invites: InviteForm[]) => {
        return invitesRequester.sendInvites(invites);
      },
      onSuccess: (response) => {
        if (response?.errors) {
          Alert.error('Houve um erro ao enviar os convites, tente novamente mais tarde.');
        } else {
          Alert.success('Convite enviado com sucesso!');
        }
      },
      onError: (error) => {
        Alert.error('Houve um erro, tente novamente mais tarde.');
      },
    });
  };

  const useAcceptInvite = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<ICreatedUser, unknown, { acceptForm: IInviteAcceptForm; token: string }>({
      mutationFn: async (data: { acceptForm: IInviteAcceptForm; token: string }) => invitesRequester.acceptInvite(data.acceptForm, data.token),
    });
  };

  const useDeleteInvite = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<boolean, unknown, string>({
      mutationFn: async (token: string) => invitesRequester.deleteInvite(token),
      onSuccess: () => {
        Alert.success('Convite excluído com sucesso!');
      },
      onError: () => {
        Alert.error('Houve um erro, tente novamente mais tarde.');
      },
    });
  };

  return {
    useGetPendingInvites,
    useGetInviteInfo,
    useSendInvites,
    useDeleteInvite,
    useAcceptInvite,
  };
};

export default useInvite;
